import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { combineReducers } from '@reduxjs/toolkit';
import { configureStore } from '~/utils/redux';
import { useMedia } from 'react-use-media';
import useAnalytics from '~/react-hooks/use-analytics';

import rootReducer from './reducers/root';
import videoReducer from './reducers/video';
import commentsReducer from './reducers/comments';
import trackingReducer from './reducers/tracking';

import NextGen from '~/Share/landingPage/NextGen';
import { LandingPageType, ReduxState, Recording } from '~/types/redux/share';
import {
  RECORDING_SHARE_MODAL_OPENED,
  RECORDING_WATCHED,
  VIDEO_RECORDING_EMBED_PLAYED,
} from '~/generated/AnalyticsTypes';
import { MessageTemplateShare } from '~/types/models';

export type RecordingLandingPageProps = {
  hideReport?: boolean;
  preview?: boolean;
  recording: Recording;
  messageTemplate: MessageTemplateShare;
};

export default function RecordingLandingPage({
  hideReport,
  preview = false,
  recording,
  messageTemplate,
}: RecordingLandingPageProps) {
  const { features, assignee } = config;
  const analytics = useAnalytics();

  const [store, setStore] = useState();

  const showSingleColumnLayout = useMedia({
    maxWidth: !preview ? 850 : 1170,
  });

  useEffect(() => {
    const bootstrap = async () => {
      const defaultAnalyticsTracking = {
        templateType: messageTemplate.template_type,
        sourceCreatedBy: recording.upload_source,
        referrer: document.referrer,
      };
      const store = await configureStore<ReduxState>({
        combineReducers,
        transformer: (state) => state,
        reducers: {
          root: rootReducer,
          video: videoReducer,
          comments: commentsReducer,
          tracking: trackingReducer,
        },
        initialState: {
          root: {
            model: recording,
            messageTemplate,
            features,
            assignee,
            type: LandingPageType.Recording,
          },
          comments: {
            comments: [],
            creating: false,
            value: '',
            errorMessage: null,
            focused: false,
          },
          video: {
            playing: false,
            progress: 0,
            error: undefined,
          },
          tracking: {
            shareModalClicked: () =>
              analytics.track(
                RECORDING_SHARE_MODAL_OPENED,
                defaultAnalyticsTracking
              ),
            videoWatched: () =>
              analytics.track(RECORDING_WATCHED, defaultAnalyticsTracking),
            videoEmbedPlayed: () =>
              analytics.track(
                VIDEO_RECORDING_EMBED_PLAYED,
                defaultAnalyticsTracking
              ),
            ctaClicked: () => undefined, //cta tracking on Recording is on BE. /r/id/click
          },
        },
      });

      setStore(store);
    };

    bootstrap();
  }, [recording, messageTemplate]);

  if (!store) return null;

  return (
    <Provider store={store}>
      <NextGen
        slug={recording.slug}
        hideReport={hideReport}
        showSingleColumnLayout={showSingleColumnLayout}
        hideSocialShareBar={preview}
      />
    </Provider>
  );
}
